import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import PostCreator from './components/PostCreator';
import FeedPost from './components/FeedPost';
import TrendingTopics from './components/TrendingTopics';
import SuggestedConnections from './components/SuggestedConnections';
import UpcomingEvents from './components/UpcomingEvents';
import { supabase } from '../../lib/supabase';

const ProfessionalFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showPostCreator, setShowPostCreator] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const feedFilters = [
    { key: 'all', label: 'Tous', icon: 'Globe' },
    { key: 'connections', label: 'Connexions', icon: 'Users' },
    { key: 'achievements', label: 'Réussites', icon: 'Trophy' },
    { key: 'performances', label: 'Performances', icon: 'Music' },
    { key: 'collaborations', label: 'Collaborations', icon: 'Handshake' }
  ];

  useEffect(() => {
    getCurrentUser();
    fetchPosts();
  }, [filter]);

  const getCurrentUser = async () => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user) {
        setCurrentUser(session?.user);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      
      let query = supabase?.from('professional_posts')?.select(`
          *,
          author:user_profiles!professional_posts_author_id_fkey (
            id,
            full_name,
            avatar_url,
            role
          ),
          author_partner:partners!inner (
            business_name,
            partner_type,
            artist_profession,
            is_verified
          )
        `)?.order('created_at', { ascending: false })?.limit(20);

      // Apply filters
      if (filter === 'achievements') {
        query = query?.eq('post_type', 'achievement');
      } else if (filter === 'performances') {
        query = query?.eq('post_type', 'performance_announcement');
      } else if (filter === 'collaborations') {
        query = query?.eq('post_type', 'collaboration');
      }

      const { data, error } = await query;

      if (error) throw error;
      
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePostCreated = async (newPost) => {
    // Refetch posts to get the new one with full data
    await fetchPosts();
    setShowPostCreator(false);
  };

  const handleInteraction = async (postId, interactionType) => {
    if (!currentUser) return;

    try {
      // Check if user already interacted
      const { data: existingInteraction } = await supabase?.from('post_interactions')?.select('id, interaction_type')?.eq('post_id', postId)?.eq('user_id', currentUser?.id)?.single();

      if (existingInteraction) {
        if (existingInteraction?.interaction_type === interactionType) {
          // Remove interaction if same type
          await supabase?.from('post_interactions')?.delete()?.eq('id', existingInteraction?.id);
        } else {
          // Update interaction type
          await supabase?.from('post_interactions')?.update({ interaction_type: interactionType })?.eq('id', existingInteraction?.id);
        }
      } else {
        // Add new interaction
        await supabase?.from('post_interactions')?.insert({
            post_id: postId,
            user_id: currentUser?.id,
            interaction_type: interactionType
          });
      }

      // Refresh posts to update counts
      await fetchPosts();
    } catch (error) {
      console.error('Error handling interaction:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Feed Professionnel - Artis.io</title>
        <meta name="description" content="Découvrez les dernières actualités de l'industrie musicale et artistique sur Artis.io" />
      </Helmet>
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-8">
            
            {/* Left Sidebar - Trending & Filters */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-surface rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Filtres</h3>
                <div className="space-y-2">
                  {feedFilters?.map((filterOption) => (
                    <Button
                      key={filterOption?.key}
                      variant={filter === filterOption?.key ? "default" : "ghost"}
                      className={`w-full justify-start ${filter === filterOption?.key ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      onClick={() => setFilter(filterOption?.key)}
                      iconName={filterOption?.icon}
                    >
                      {filterOption?.label}
                    </Button>
                  ))}
                </div>
              </div>

              <TrendingTopics />
              <UpcomingEvents />
            </div>

            {/* Main Feed */}
            <div className="lg:col-span-6 space-y-6">
              {/* Post Creation */}
              <div className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-primary-foreground font-semibold">
                      {currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    className="flex-1 justify-start text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPostCreator(true)}
                  >
                    Partager une actualité, une réussite...
                  </Button>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Camera"
                    onClick={() => setShowPostCreator(true)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Photo
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Video"
                    onClick={() => setShowPostCreator(true)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Vidéo
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Calendar"
                    onClick={() => setShowPostCreator(true)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Événement
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trophy"
                    onClick={() => setShowPostCreator(true)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Réussite
                  </Button>
                </div>
              </div>

              {/* Posts Feed */}
              {loading ? (
                <div className="space-y-6">
                  {[1, 2, 3]?.map((i) => (
                    <div key={i} className="bg-surface rounded-lg border border-border p-6">
                      <div className="animate-pulse">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="w-12 h-12 bg-muted rounded-full"></div>
                          <div className="space-y-2 flex-1">
                            <div className="h-4 bg-muted rounded w-1/4"></div>
                            <div className="h-3 bg-muted rounded w-1/6"></div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="h-4 bg-muted rounded w-3/4"></div>
                          <div className="h-4 bg-muted rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : posts?.length > 0 ? (
                <div className="space-y-6">
                  {posts?.map((post) => (
                    <FeedPost
                      key={post?.id}
                      post={post}
                      currentUser={currentUser}
                      onInteraction={handleInteraction}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-surface rounded-lg border border-border p-8 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📱</span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Aucun contenu pour le moment
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Soyez le premier à partager une actualité dans votre réseau artistique !
                  </p>
                  <Button onClick={() => setShowPostCreator(true)} iconName="Plus">
                    Créer un post
                  </Button>
                </div>
              )}
            </div>

            {/* Right Sidebar - Suggestions */}
            <div className="lg:col-span-3 space-y-6">
              <SuggestedConnections />
              
              {/* Professional News */}
              <div className="bg-surface rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Actualités Industrie
                </h3>
                <div className="space-y-4">
                  {[
                    { title: "Nouveau festival de musique électronique à Lyon", category: "Festival", time: "2h" },
                    { title: "Les streaming payent-ils assez les artistes ?", category: "Industrie", time: "5h" },
                    { title: "Formation production musicale - Places limitées", category: "Formation", time: "1j" }
                  ]?.map((news, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 hover:bg-muted rounded-lg cursor-pointer transition-colors">
                      <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground line-clamp-2">
                          {news?.title}
                        </h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs text-muted-foreground">{news?.category}</span>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">{news?.time}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Post Creator Modal */}
      {showPostCreator && (
        <PostCreator
          onClose={() => setShowPostCreator(false)}
          onPostCreated={handlePostCreated}
        />
      )}
    </div>
  );
};

export default ProfessionalFeed;